import { yupResolver } from '@hookform/resolvers/yup';
import { Button, Dialog, DialogActions, DialogContent } from '@mui/material';
import { FC, useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { DialogTitleWithClose, ISupportee } from '../../../shared';
import { useRelations, useSaveSupportee } from '../../hooks';
import { supporteeMapperFromForm, supporteeMapperToForm } from '../../mappers';
import { ISupporteeForm, SupporteeDefaults } from '../../models';
import { useSupporteeSchema } from '../../validators';
import { SupporteeInputs } from '../support-case-create/supportee-inputs.component';

interface Props {
    open: boolean;
    supportCaseId: string;
    supportee?: ISupportee | null;
    handleClose: () => void;
}

export const SaveSupporteeModal: FC<Props> = ({ handleClose, open, supportCaseId, supportee }) => {
    const { t } = useTranslation();

    const { mutateAsync: saveSupportee } = useSaveSupportee(supportCaseId);

    const schema = useSupporteeSchema();
    const form = useForm<ISupporteeForm>({
        resolver: yupResolver(schema),
        mode: 'onSubmit',
    });

    useEffect(() => {
        if (supportee) {
            form.reset(supporteeMapperToForm(supportee));
        } else {
            form.reset(SupporteeDefaults);
        }
    }, [form, supportee, open]);

    const relations = useRelations();
    const onSubmit = async (data: ISupporteeForm) => {
        await saveSupportee(supporteeMapperFromForm(data, relations));
        handleClose();
    };

    return (
        <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
            <FormProvider {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)} noValidate>
                    <DialogTitleWithClose onClose={handleClose}>
                        {supportee ? t('editSupportee') : t('addSupportee')}
                    </DialogTitleWithClose>

                    <DialogContent>
                        <SupporteeInputs dialogLayout />
                    </DialogContent>

                    <DialogActions>
                        <Button type="submit" variant="contained" color="primary">
                            {t('save')}
                        </Button>
                        <Button onClick={handleClose} color="secondary">
                            {t('cancel')}
                        </Button>
                    </DialogActions>
                </form>
            </FormProvider>
        </Dialog>
    );
};
